import { useNavigate, useParams } from "react-router";
import { AuthContext, useAuth } from "../../utility/useAuth";
import { useEffect, useRef, useState } from "react";
import { IJobDetails } from "../../types/IJobDetails";
import {
  deleteDocument,
  getDocumentContent,
  getDocuments,
  getJobDetails,
  uploadDocument,
} from "../../services/ApiService";
import { ToastUtility } from "@syncfusion/ej2-react-notifications";
import { PartOrderWithImages } from "../../types/PartOrderWithImages";
import { Col, Container, Row } from "react-grid-system";
import {
  MenuComponent,
  MenuEventArgs,
  MenuItemModel,
} from "@syncfusion/ej2-react-navigations";
import { useStoreActions } from "../../store";
import { HeaderMode } from "../../types/HeaderMode";
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Resize,
  RowSelectEventArgs,
  SelectionSettingsModel,
} from "@syncfusion/ej2-react-grids";
import {
  ListBoxChangeEventArgs,
  ListBoxComponent,
} from "@syncfusion/ej2-react-dropdowns";
import { getPathItem } from "../../utility/pathHelper";
import { PathItem } from "../../types/PathItem";
import { DeleteImageModal } from "../../components/Modals/DeleteImageModal/DeleteImageModal";
import { AddImageModal } from "../../components/Modals/AddImageModal/AddImageModal";
import { CaptureImageModal } from "../../components/Modals/CaptureImageModal/CaptureImageModal";
import { ViewImageModal } from "../../components/Modals/ViewImageModal/ViewImageModal";
import { StyledJobDocumentsPage } from "./JobDocumentsPage.styles";
import { PartOrderWithDocuments } from "../../types/PartOrderWithDocuments";
import { useFilePicker } from "use-file-picker";
import { YesNoModal } from "../../components/Modals/YesNoModal/YesNoModal";
import { FileContent } from "use-file-picker/dist/interfaces";

export const JobDocumentsPage: React.FC = () => {
  let menuObj: MenuComponent | null;
  let listboxObj: ListBoxComponent | null;
  const { jobNumber } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const { appSettings: action } = useStoreActions((actions) => actions);

  const [jobDetails, setJobDetails] = useState<IJobDetails>();
  const [partsOrders, setPartOrders] = useState<PartOrderWithDocuments[]>([]);
  const [selectedPartsOrder, setSelectedPartOrder] =
    useState<PartOrderWithDocuments>();
  const [selectedDocuments, setSelectedDocuments] = useState<PathItem[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<PathItem>();
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);

  const { openFilePicker, filesContent, loading } = useFilePicker({
    readAs: "ArrayBuffer",
    accept: "*",
    multiple: true,
    readFilesContent: true,
    onFilesSuccessfullySelected: ({ plainFiles, filesContent }) => {
      // this callback is called when there were no validation errors
      console.log("onFilesSuccessfullySelected", plainFiles, filesContent);
      uploadFiles(`documents${selectedPartsOrder?.weldId}`, filesContent)
        .then(() => {
          if (jobDetails) {
            loadPartOrders(jobDetails).then((orders) => {
              const order = orders.find(
                (o) => o.weldId === selectedPartsOrder?.weldId,
              );
              setSelectedPartOrder(order);
              const documents = order?.documents.map((document) =>
                getPathItem(document),
              );
              if (documents) {
                setSelectedDocuments(documents);
              }
            });
          }
        })
        .catch((error) => {
          handleError(error, "Failed to upload document(s).");
        });
    },
  });

  async function uploadFiles(
    folder: string,
    files: FileContent<ArrayBuffer>[],
  ): Promise<void> {
    for (const file of files) {
      await uploadDocument(auth, folder, file.name, new Blob([file.content]));
    }
  }

  async function loadPartOrders(
    jobDetails: IJobDetails,
  ): Promise<PartOrderWithDocuments[]> {
    const documents = await getDocuments(auth, "documents");

    let orders: PartOrderWithDocuments[] = [];
    for (let i = 1; i <= jobDetails.qtyOrdered; i++) {
      const weld = `${jobNumber}-${i}`;
      const partOrderDocuments = documents.filter((d) =>
        d.startsWith(`documents${weld}`),
      );

      orders.push({
        weldId: weld,
        documentCount: partOrderDocuments.length,
        documents: partOrderDocuments,
      });
    }

    setPartOrders(orders);
    return orders;
  }

  useEffect(() => {
    const fetchData = async (jobNumber: string) => {
      // get the data from the api
      const jobDetails = await getJobDetails(auth, jobNumber);

      setJobDetails(jobDetails);

      await loadPartOrders(jobDetails);
    };
    if (jobNumber) {
      fetchData(jobNumber)
        // make sure to catch any error
        .catch((error) => {
          const message = `Failed to get job details. Error: ${error}`;
          console.error(`Failed to get job details. Error: ${error}`);
          ToastUtility.show(message, "Error", 2000);
        });
    }

    if (jobNumber) {
      action.setJobNumber(jobNumber);
      action.setHeaderMode(HeaderMode.ProductionOrderImages);
      getJobDetails(auth, jobNumber)
        .then((data) => {
          setJobDetails(data);
        })
        .catch((error) => {
          const message = `Failed to get job details. Error: ${error}`;
          console.error(`Failed to get job details. Error: ${error}`);
          ToastUtility.show(message, "Error", 2000);
        });
    }
  }, [jobNumber]);

  const menuItems: MenuItemModel[] = [
    { id: "back", text: "Back", iconCss: "e-icons e-back" },
    { id: "addDocument", text: "Add", iconCss: "e-icons e-folder-open" },
    { id: "deleteDocument", text: "Delete", iconCss: "e-icons e-trash" },
    {
      id: "downloadDocument",
      text: "Download",
      iconCss: "e-icons e-align-bottom",
    },
  ];

  const toolTips = new Map<string, string>([
    ["addDocument", "Add Document"],
    ["deleteDocument", "Delete Document"],
    ["downloadDocument", "Download Document"],
  ]);

  let disableItems: string[] = ["Delete", "Download"];

  function beforeItemRender(args: MenuEventArgs): void {
    args.element.setAttribute("title", toolTips.get(args.item.id ?? "") ?? "");
  }

  function created(): void {
    // Disable items
    if (menuObj) {
      menuObj.enableItems(["Add", "Capture"], false, false);
      menuObj.enableItems(["Delete", "Download"], false, false);
    }
  }

  function selectMenuItem(args: MenuEventArgs): void {
    const id = args.item.id;

    if (id === "deleteDocument") {
      setShowDeleteDocumentModal(true);
    } else if (id === "addDocument") {
      openFilePicker();
    } else if (id === "downloadDocument" && selectedDocument) {
      getDocumentContent(
        auth,
        selectedDocument.folder,
        selectedDocument.fileName,
      )
        .then((data) => {
          if (data) {
            const href = URL.createObjectURL(data);
            const link = document.createElement("a");
            link.href = href;
            link.setAttribute("download", selectedDocument.fileName); //or any other extension
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
          }
        })
        .catch((error) => {
          const message = `Failed to get document content. Error: ${error}`;
          console.error(message);
          //ToastUtility.show(message, "Error", 2000);
          return;
        });
    } else if (id === "back") {
      navigate(-1);
    }
  }

  function onPartsOrderSelected(args: RowSelectEventArgs) {
    if (menuObj) {
      menuObj.enableItems(disableItems, false, false);
      menuObj.enableItems(["Add", "Capture"], false, false);
    }
    const partOrder = args.data as PartOrderWithDocuments;
    if (partOrder) {
      setSelectedPartOrder(partOrder);
      const documents = partOrder.documents.map((document) =>
        getPathItem(document),
      );
      if (documents) {
        setSelectedDocuments(documents);
      }

      if (menuObj) {
        menuObj.enableItems(["Add", "Capture"], true, false);
      }
    }
  }

  function onDocumentSelected(args: ListBoxChangeEventArgs) {
    if (menuObj) {
      menuObj.enableItems(disableItems, false, false);
    }
    if (args.items && args.items.length === 1) {
      setSelectedDocument(args.items[0] as PathItem);
      if (menuObj) {
        menuObj.enableItems(disableItems, true, false);
      }
    }
  }

  const selectionSettings: SelectionSettingsModel = {
    mode: "Row",
    type: "Single",
  };

  function handleError(error: any, prefix?: string) {
    const message = `${prefix} Error: ${error}`;
    console.error(message);
    ToastUtility.show(message, "Error", 2000);
  }

  function onDeleteDocument(document: PathItem) {
    setShowDeleteDocumentModal(false);
    const partOrder = selectedPartsOrder;
    setSelectedPartOrder(undefined);
    setSelectedDocuments([]);
    deleteDocument(auth, document.folder, document.fileName)
      .then(() => {
        if (jobDetails) {
          loadPartOrders(jobDetails).then((orders) => {
            const order = orders.find((o) => o.weldId === partOrder?.weldId);
            setSelectedPartOrder(order);
            const documents = order?.documents.map((document) =>
              getPathItem(document),
            );
            if (documents) {
              setSelectedDocuments(documents);
            }
          });
        }
      })
      .catch((error) => {
        handleError(error, "Failed to delete document.");
      });
  }

  return (
    <StyledJobDocumentsPage>
      {partsOrders ? (
        <Container fluid>
          <Row align="stretch">
            <MenuComponent
              ref={(scope: MenuComponent) => (menuObj = scope)}
              items={menuItems}
              select={selectMenuItem}
              beforeItemRender={beforeItemRender}
              created={created}
            />
          </Row>
          <Row align="stretch">
            <Col sm={6}>
              <GridComponent
                id="Grid"
                dataSource={partsOrders}
                selectionSettings={selectionSettings}
                //selectedRowIndex={0}
                allowResizing
                height={600}
                gridLines="Both"
                rowSelected={onPartsOrderSelected}
              >
                <Inject services={[Resize]} />
                <ColumnsDirective>
                  <ColumnDirective
                    field="weldId"
                    isPrimaryKey={true}
                    headerText="Part Production Order"
                    width="150"
                    textAlign="Left"
                  />
                  <ColumnDirective
                    field="documentCount"
                    isPrimaryKey={true}
                    headerText="Document Count"
                    width="100"
                    textAlign="Left"
                  />
                </ColumnsDirective>
              </GridComponent>
            </Col>
            {selectedPartsOrder && (
              <Col>
                <ListBoxComponent
                  id="listbox"
                  ref={(scope: ListBoxComponent) => (listboxObj = scope)}
                  height={650}
                  dataSource={selectedDocuments}
                  selectionSettings={{ mode: "Single" }}
                  change={onDocumentSelected}
                  fields={{ text: "fileName" }}
                />
              </Col>
            )}
          </Row>
        </Container>
      ) : (
        <>Loading...</>
      )}
      {showDeleteDocumentModal && selectedDocument && (
        <YesNoModal
          open={showDeleteDocumentModal}
          onYes={() => onDeleteDocument(selectedDocument)}
          onNo={() => {
            setShowDeleteDocumentModal(false);
          }}
          message={`Are you sure that you want to delete ${selectedDocument.fileName} document?`}
        />
      )}
    </StyledJobDocumentsPage>
  );
};
